import React, { useEffect, useState } from 'react';
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";

import DynamicSelect from "./filter/DynamicSelect";
import DownloadSplitButton from "./filter/DownloadSplitButton";
import { ChartToggle } from "./filter/ChartToggle";
import LineChart from "./charts/LineChart";
import BarChart from "./charts/BarChart";
import { SettingsPopover } from "../../common/settings/SettingsPopover";
import ExportDialog from "./export/ExportDialog";
import { resetFilters, setChartType, setCompareBy, setSelectedProps } from "../../state/actions/filterActions";
import { checkBoxLabels, compareByToolTipComponents, compareMax, DropdownTooltips } from "./filter/FilterConsts";
import {
    selectChartType,
    selectCompareBy,
    selectCompareByList,
    selectIsFilterValid,
    selectSelectedProps
} from "../../state/selectors/filterSelectors";
import { selectFromYear, selectToYear, selectYearRange } from "../../state/selectors/settingsSelectors";
import { selectFilterOptions } from "../../state/selectors/dataSelectors";
import {
    setBarChartData,
    setFilterOptions,
    setLegendLabels,
    setLineChartData,
    setNumberOfSamples,
    setNumberOfSamplesXAxis
} from "../../state/actions/dataActions";
import { setFromYear, setToYear, setYearRange } from "../../state/actions/settingsActions";
import YearRangeSlider from '../../common/YearRangeSlider';
import { API_BASE_URL } from "../../config";

function DanmapApp() {
    const dispatch = useDispatch()
    const fromYear = useSelector(selectFromYear)
    const toYear = useSelector(selectToYear)
    const yearRange = useSelector(selectYearRange)

    const selectedProps = useSelector(selectSelectedProps)
    const compareBy = useSelector(selectCompareBy)
    const compareByList = useSelector(selectCompareByList)
    const chartType = useSelector(selectChartType)

    const filterOptions = useSelector(selectFilterOptions)
    const isFilterValid = useSelector(selectIsFilterValid)

    const [startIcon, setStartIcon] = useState<React.ReactNode>(<GetAppIcon />);
    const [showMore, setShowMore] = useState<boolean>(false);
    const [sliderValue, setSliderValue] = useState<number[]>([fromYear, toYear]);

    const legalText = 'Graph generated using DANMAP-software. The software is provided "AS IS" without any warrenty or liablity from DANMAP or the Technical University of Denmark.';

    const dataProps = ['finding', 'animal', 'origin', 'antibiotic']

    const apiBaseUrl = API_BASE_URL || "";
    const withApiBaseUrl = (path: string) =>
        `${apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

    useEffect(() => {
        fetchYearRange(withApiBaseUrl('/api/get_year_range'))
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        setSliderValue([fromYear, toYear]);
    }, [fromYear, toYear]);

    useEffect(() => {
        const filtersReset = {}
        Object.keys(selectedProps).filter(key => !compareByList.includes(key) && selectedProps[key as keyof typeof selectedProps]?.length !== 1)
            .forEach((k: string) => {
                // @ts-ignore
                filtersReset[k] = [selectedProps[k][0]]
            })
        if (Object.keys(filtersReset).length !== 0) {
            dispatch(setSelectedProps(filtersReset))
        }
        // eslint-disable-next-line
    }, [compareByList])


    const createParams = () => {
        let params: any[] = [];
        compareByList.length > 0 && compareByList.forEach(el => params.push(['compare-by', el]))

        //If the selected prop array contains null (i.e. no options are selected), do not add to the params
        dataProps.forEach(prop => !selectedProps[prop as keyof typeof selectedProps]?.includes(null)
            && selectedProps[prop as keyof typeof selectedProps]?.forEach(selected => {
                params.push([prop, selected])
            }))
        params.push(['fromYear', fromYear])
        params.push(['toYear', toYear])
        return params;
    }
    const [href, setHref] = useState<string>(createURL(`/api/get_full_csv`, createParams()));


    useEffect(() => {
        // Only retrieve any data if at least 1 compareby value is selected is defined
        if (compareByList.length) {
            const params = createParams();
            fetchDataSafely(createURL(`/api/${chartType === 'line' ? 'line_chart_data' : 'dataset'}`, params))
            fetchFilterOptions(createURL(`/api/filter_options`, params))
        }
        // eslint-disable-next-line
    }, [selectedProps, chartType, compareBy])

    useEffect(() => {
        fetchDataSafely(createURL(`/api/${chartType === 'line' ? 'line_chart_data' : 'dataset'}`, createParams()))
        // eslint-disable-next-line
    }, [fromYear, toYear])

    const fetchDataSafely = (url: string) => {
        if (!compareByList.length) {
            return;
        }
        // Only fetch data if none of the params is undefined, otherwise fetch both data and filter options
        if (!Object.values(selectedProps).some(x => (x === null || x === undefined || x.length < 1 || x.includes(null)))) {
            fetchData(url)
        }
    }

    const switchChartType = (newChartType: string) => {
        dispatch(setChartType(newChartType))
    };

    function createURL(path: string, params: string[][]): string {
        let url = withApiBaseUrl(path);
        const searchParams = new URLSearchParams(params).toString();
        if (params.length > 0) {
            url = url.concat(`?${searchParams}`)
        }
        return url;
    }

    function fetchData(url: string) {
        fetch(url)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Request failed: ${res.status} ${res.statusText}`);
                }
                return res.json();
            })
            .then(
                (result) => {
                    dispatch(setLegendLabels(result.legendLabels))
                    dispatch(setNumberOfSamples(result.numberOfSamples))
                    dispatch(setNumberOfSamplesXAxis(result.numberOfSamplesXAxis))
                    if (result.chartType === 'bar')
                        dispatch(setBarChartData(result.data))
                    else if (result.chartType === 'line')
                        dispatch(setLineChartData(result.data))
                }
            )
            .catch((err) => {
                console.error('Failed to fetch chart data', err);
            });
    }

    function fetchFilterOptions(url: string) {
        fetch(url)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Request failed: ${res.status} ${res.statusText}`);
                }
                return res.json();
            })
            .then(
                (result) => {
                    dispatch(setFilterOptions(result))
                }
            )
            .catch((err) => {
                console.error('Failed to fetch filter options', err);
            });
    }

    function fetchYearRange(url: string) {
        fetch(url)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Request failed: ${res.status} ${res.statusText}`);
                }
                return res.json();
            })
            .then(
                (result) => {
                    dispatch(setYearRange(result))
                    dispatch(setFromYear(result.minYear))
                    dispatch(setToYear(result.maxYear))
                }
            )
            .catch((err) => {
                console.error('Failed to fetch year range', err);
            });
    }

    function handleDownloadDataset(event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) {
        if (index === 0) {
            setHref(createURL("/api/get_full_csv", createParams()))
            setStartIcon(<GetAppIcon />)
        } else if (index === 1) {
            setHref(createURL(`/api/filtered_csv`, createParams()))
            setStartIcon(<GetAppOutlinedIcon />)
        }
    }

    const handleChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setCompareBy({ [event.target.name]: event.target.checked }))
    };

    const handleResetFilters = () => {
        dispatch(resetFilters());
    };

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        setSliderValue(newValue as number[]);
    };

    const handleSliderChangeCommitted = (event: Event | React.SyntheticEvent<Element, Event>, newValue: number | number[]) => {
        const [min, max] = newValue as number[];
        dispatch(setFromYear(min));
        dispatch(setToYear(max));
    };

    return (
        <>
            <div className="mainContainer">
                <div className="filterArea">
                    <div className="headerTitle">
                        <FilterListIcon />
                        <Typography align='left' className="headerTitleLabel">Filter</Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleResetFilters}
                            sx={{marginLeft: 'auto'}}
                        >
                            Reset filters
                        </Button>
                    </div>
                    <span className={clsx("selectorContainer")}>
                        {dataProps.map(prop => (
                            <React.Fragment key={prop + '_filter_fragment'}>
                                {/*It's surrounded with div-s because it can hold a ref as opposed to the function component.
                    Source: https://stackoverflow.com/questions/57527896/material-ui-tooltip-doesnt-display-on-custom-component-despite-spreading-props*/}
                                <div
                                    className={clsx("dynamicSelect", compareBy[prop as keyof typeof compareBy] && "compareBy")}
                                    key={prop + '_filter'}
                                >
                                    <Tooltip
                                        title={compareByToolTipComponents[prop as keyof typeof compareByToolTipComponents]}>
                                        <FormControlLabel value={prop}
                                            control={<Checkbox
                                                checked={compareBy[prop as keyof typeof compareBy]}
                                                onChange={handleChecked}
                                                name={prop}
                                                disabled={(compareByList.length >= compareMax &&
                                                    !compareBy[prop as keyof typeof compareBy])} />}
                                            label=""
                                        />
                                    </Tooltip>
                                    <Tooltip title={DropdownTooltips()[prop as keyof typeof checkBoxLabels]}
                                        placement="top">
                                        <DynamicSelect
                                            id={`${prop}-options`}
                                            options={filterOptions[prop as keyof typeof filterOptions].sort()}
                                            label={checkBoxLabels[prop as keyof typeof checkBoxLabels]}
                                            disabled={!compareByList.length || compareByList.length > compareMax}
                                            {...(compareBy[prop as keyof typeof compareBy] ? {
                                                type: 'multiple',
                                                valueMultiple: selectedProps[prop as keyof typeof selectedProps],
                                                setValueMultiple: newValue => dispatch(setSelectedProps({
                                                    [prop]: newValue
                                                }))
                                            } : {
                                                type: 'single',
                                                valueSingle: selectedProps[prop as keyof typeof selectedProps]![0],
                                                setValueSingle: newValue => dispatch(setSelectedProps({
                                                    [prop]: [newValue]
                                                }))
                                            })}
                                        />
                                    </Tooltip>
                                </div>
                            </React.Fragment>
                        ))}
                        <div className="dynamicSelect">
                            <YearRangeSlider
                                min={yearRange.minYear}
                                max={yearRange.maxYear}
                                value={sliderValue}
                                onChange={handleSliderChange}
                                onChangeCommitted={handleSliderChangeCommitted}
                            />
                        </div>
                    </span>
                </div>
                <div className="chartArea">
                    <div className="buttonContainer">
                        <div className="buttonGrLeft">
                            <ExportDialog esc={false} />
                            <DownloadSplitButton options={["Full dataset", "Filtered dataset"]}
                                handleOptionClick={handleDownloadDataset}
                                href={href}
                                startIcon={startIcon}
                                className="downloadSplitBtn"
                                esc={false}
                            />
                        </div>
                        <div className="buttonGrRight">
                            <ChartToggle className="chartToggle" defaultType="line" switchChartType={switchChartType} />
                            <SettingsPopover esc={false} />
                        </div>
                    </div>
                    <div key={chartType} className={clsx("chartWrapper", !isFilterValid && "disabled")}>
                        {!isFilterValid && (
                            <span className="disabledOverlayText">Please define all filters</span>)}
                        {chartType === 'bar' && (<BarChart isInteractive={isFilterValid} />)}
                        {chartType === 'line' && (<LineChart isInteractive={isFilterValid} />)}
                    </div>
                </div>
            </div>

            <div>
                <Typography variant="caption" className="legalText">
                    {showMore ? legalText : `${legalText.substring(0, 300)}... `}
                    <button className="showMoreBtn"
                        onClick={() => setShowMore(!showMore)}>{showMore ? "Show less" : "Show more"}</button>
                    <br />
                    © 2024 Technical University of Denmark
                </Typography>
            </div>
        </>
    );
}

export default DanmapApp;
